import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, X, Edit3 } from "lucide-react";
import { adminListKnowledge, createKnowledgeArticle, updateKnowledgeArticle, deleteKnowledgeArticle } from "@/lib/api";

const CATEGORIES = ["best_practice", "gap", "policy", "market_link", "faq", "other"];
const LANGS = ["en", "hi", "mr"];

export default function AdminKnowledge() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => adminListKnowledge().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    await deleteKnowledgeArticle(id); load(); toast.success(t("toast.deleted"));
  };

  return (
    <div data-testid="admin-knowledge">
      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="w-6 h-6 text-[var(--tr-deep-green)]" />
        <h2 className="tr-section-title text-3xl">{t("knowledge.admin_title")}</h2>
      </div>
      <p className="text-[var(--tr-muted)] leading-relaxed max-w-3xl">{t("knowledge.admin_body")}</p>
      <div className="mt-6">
        <button data-testid="kd-new-btn" onClick={() => setShowNew(true)} className="tr-pill-btn tr-btn-primary"><Plus className="w-4 h-4" /> {t("knowledge.new_article")}</button>
      </div>
      {!items ? <div className="p-6 text-[var(--tr-muted)]">{t("common.loading")}</div>
        : (
          <div className="tr-card mt-6 overflow-hidden" data-testid="kd-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--tr-sage)]/50"><tr>
                  {["Title", "Category", "Language", "Published", "Updated"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>))}
                  <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
                </tr></thead>
                <tbody>
                  {items.map((a) => (
                    <tr key={a.id} data-testid={`kd-row-${a.id}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)]">
                      <td className="px-4 py-3 font-semibold text-[var(--tr-deep-green)]">{a.title}</td>
                      <td className="px-4 py-3 text-xs uppercase">{a.category}</td>
                      <td className="px-4 py-3 text-xs uppercase">{a.language}</td>
                      <td className="px-4 py-3">{a.published ? "✓" : "—"}</td>
                      <td className="px-4 py-3 text-xs text-[var(--tr-muted)]">{new Date(a.updated_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button data-testid={`kd-edit-${a.id}`} onClick={() => setEditing(a)} className="text-[var(--tr-deep-green)] mr-3 hover:underline"><Edit3 className="w-4 h-4 inline" /></button>
                        <button data-testid={`kd-delete-${a.id}`} onClick={() => remove(a.id)} className="text-red-600 hover:underline"><Trash2 className="w-4 h-4 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      {(showNew || editing) && (
        <ArticleModal
          article={editing}
          onClose={() => { setEditing(null); setShowNew(false); }}
          onSaved={() => { setEditing(null); setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}

function ArticleModal({ article, onClose, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState(() => ({
    title: article?.title || "",
    category: article?.category || "best_practice",
    language: article?.language || "en",
    summary: article?.summary || "",
    body: article?.body || "",
    tags: (article?.tags || []).join(", "),
    resource_url: article?.resource_url || "",
    published: article ? !!article.published : true,
  }));
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async () => {
    if (!f.title || !f.body) { toast.error(t("toast.fill_required")); return; }
    const payload = {
      ...f,
      tags: f.tags.split(",").map((x) => x.trim()).filter(Boolean),
      resource_url: f.resource_url || null,
      summary: f.summary || null,
    };
    try {
      if (article) await updateKnowledgeArticle(article.id, payload);
      else await createKnowledgeArticle(payload);
      toast.success(t("toast.saved")); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" data-testid="kd-modal">
      <div className="tr-card max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{article ? t("knowledge.f_title") : t("knowledge.new_article")}</h3>
          <button onClick={onClose} data-testid="kd-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2"><span className="tr-label">{t("knowledge.f_title")}*</span><input data-testid="kd-title" className="tr-input" value={f.title} onChange={set("title")} /></label>
          <label className="block"><span className="tr-label">{t("knowledge.f_category")}</span>
            <select data-testid="kd-category" className="tr-input" value={f.category} onChange={set("category")}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{t(`knowledge.cat_${c}`)}</option>)}
            </select>
          </label>
          <label className="block"><span className="tr-label">{t("knowledge.f_language")}</span>
            <select data-testid="kd-language" className="tr-input" value={f.language} onChange={set("language")}>
              {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2"><span className="tr-label">{t("knowledge.f_summary")}</span><textarea data-testid="kd-summary" className="tr-input tr-textarea" value={f.summary} onChange={set("summary")} /></label>
          <label className="block md:col-span-2"><span className="tr-label">{t("knowledge.f_body")}*</span><textarea data-testid="kd-body" className="tr-input tr-textarea min-h-[200px]" value={f.body} onChange={set("body")} /></label>
          <label className="block"><span className="tr-label">{t("knowledge.f_tags")}</span><input data-testid="kd-tags" className="tr-input" value={f.tags} onChange={set("tags")} /></label>
          <label className="block"><span className="tr-label">{t("knowledge.f_url")}</span><input data-testid="kd-url" className="tr-input" value={f.resource_url} onChange={set("resource_url")} /></label>
        </div>
        <label className="mt-4 flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="kd-published" checked={f.published} onChange={set("published")} className="w-5 h-5 accent-[var(--tr-deep-green)]" />
          <span className="text-sm text-[var(--tr-deep-green)]">{t("knowledge.f_published")}</span>
        </label>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="tr-pill-btn tr-btn-outline">{t("common.cancel")}</button>
          <button data-testid="kd-save" onClick={save} className="tr-pill-btn tr-btn-primary">{t("knowledge.save_article")}</button>
        </div>
      </div>
    </div>
  );
}
